import {
  TruckStopCargoType,
  TruckStopOperationDetails,
} from '@/@types/generated'
import { CheckIcon, XIcon } from 'lucide-react'
import type { PropsWithChildren } from 'react'

interface CompanyOperationDetailsProps {
  operationDetails: TruckStopOperationDetails
}

interface OperationGroupProps {
  label: string
  values: string[]
}

interface CargoTypesGroupProps {
  cargoTypes: TruckStopCargoType[]
}

export function GroupLabel({ children }: PropsWithChildren) {
  return (
    <span className="text-muted-foreground text-xs font-bold tracking-wide uppercase">
      {children}
    </span>
  )
}

function OperationGroup({ label, values }: OperationGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <GroupLabel>{label}</GroupLabel>
      {values.length > 0 ? (
        <ul className="flex flex-col">
          {values.map((value) => (
            <li key={value}>{value}</li>
          ))}
        </ul>
      ) : (
        <span>{'—'}</span>
      )}
    </div>
  )
}

// TRK-005: every cargo type, a green check for each the carrier has and a red X
// for each it does not. Hazmat is the certificate's, with no hint of its source
function CargoTypesGroup({ cargoTypes }: CargoTypesGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <GroupLabel>Cargo Types</GroupLabel>
      <ul className="flex flex-col gap-1">
        {cargoTypes.map(({ name, applies }) => (
          <li key={name} className="flex items-center gap-2">
            {applies ? (
              <CheckIcon aria-hidden className="size-4 text-green-600" />
            ) : (
              <XIcon aria-hidden className="size-4 text-red-600" />
            )}
            {name}
            <span className="sr-only">{applies ? 'Yes' : 'No'}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// TRK-004: the carrier's operation details from SAFER, as labelled groups
// laid out like SAFER's company snapshot, in the order the card's mock-up uses
export function CompanyOperationDetails({
  operationDetails,
}: CompanyOperationDetailsProps) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 text-sm sm:grid-cols-3">
      <OperationGroup
        label={`Carrier Operation${operationDetails.carrierOperations.length > 1 ? 's' : ''}`}
        values={operationDetails.carrierOperations}
      />
      <OperationGroup
        label={`Carrier Classification${operationDetails.operationClassifications.length > 1 ? 's' : ''}`}
        values={operationDetails.operationClassifications}
      />
      <CargoTypesGroup cargoTypes={operationDetails.cargoTypes} />
    </div>
  )
}
